import { useState } from 'react';
import { Business, ServiceBooking } from '@turizi/shared';
import { Button, Loader, Popover, NumberInput } from '@mantine/core';
import BookingSteps from '@/components/service-booking/booking-steps';
import { useTranslation } from 'react-i18next';
import { IconTrash, IconPlus, IconMinus } from '@tabler/icons-react';
import { useDeleteServiceBooking, useUpdateServiceBooking } from '@/hooks/api-hooks';
import { TagIcon } from '@/lib/tag-utils';

interface Props {
    booking: ServiceBooking;
    readOnly?: boolean;
}

export default function ServiceBookingInServiceOrder({
    booking,
    readOnly = false,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [showSteps, setShowSteps] = useState(false);
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [localSlots, setLocalSlots] = useState<number>(booking.inventory_slots ?? 1);

    const {
        mutate: deleteMutation,
        isPending: deleting
    } = useDeleteServiceBooking();

    const {
        mutate: updateMutation,
        isPending: updating
    } = useUpdateServiceBooking();

    const handleSlotsChange = (value: number) => {
        updateMutation({
            id: booking.id,
            inventory_slots: value
        });
    };

    const service = booking.service_data;

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm(t('confirm_delete') as string)) {
            deleteMutation({ id: booking.id });
        }
    };

    const selectedProviderName = booking.selected_provider?.name || service?.providers?.find(
        (p: Business) => p.id === booking.selected_provider_id
    )?.name;

    return (
        <tr
            className="hover:bg-gray-50/50 cursor-pointer transition-colors"
            onClick={() => setShowSteps(true)}
        >
            <td>
                {showSteps && (
                    <div onClick={(e) => e.stopPropagation()}>
                        <BookingSteps
                            serviceBookingId={booking.id}
                            opened={showSteps}
                            onClose={() => setShowSteps(false)}
                        />
                    </div>
                )}
                <div className="font-semibold flex gap-1 items-center px-2">
                    <TagIcon name={service.category} size="xxs" noBackground hideText />
                    {selectedProviderName || t('no_provider')}
                </div>
            </td>
            <td>
                <div className="flex justify-between items-center pr-4">
                    {/* Slots Input */}
                    {!readOnly ? (
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <Popover
                                position="bottom"
                                withArrow
                                shadow="md"
                                trapFocus
                            >
                                <Popover.Target>
                                    <div className="px-2 py-0.5 hover:bg-gray-100 rounded text-center cursor-pointer font-bold text-gray-400 text-[11px]  border border-gray-100">
                                        {booking.inventory_slots || '-'}
                                    </div>
                                </Popover.Target>
                                <Popover.Dropdown p="xs">
                                    <div className="flex flex-col gap-2">
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                            {t('inventory_slots')}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Button
                                                size="xs"
                                                variant="subtle"
                                                color="gray"
                                                className="p-0 min-w-[24px] h-7 w-7 hover:bg-gray-100 rounded transition-colors"
                                                onClick={() => setLocalSlots(prev => Math.max(0, prev - 1))}
                                                disabled={localSlots <= 0}
                                            >
                                                <IconMinus size={12} stroke={2.5} className="text-gray-500" />
                                            </Button>
                                            <NumberInput
                                                size="sm"
                                                w={65}
                                                min={0}
                                                value={localSlots}
                                                onChange={(val) => setLocalSlots(Number(val))}
                                                hideControls
                                            />
                                            <Button
                                                size="xs"
                                                variant="subtle"
                                                color="gray"
                                                className="p-0 min-w-[24px] h-7 w-7 hover:bg-gray-100 rounded transition-colors"
                                                onClick={() => setLocalSlots(prev => prev + 1)}
                                            >
                                                <IconPlus size={12} stroke={2.5} className="text-gray-500" />
                                            </Button>
                                        </div>
                                        <Button
                                            size="xs"
                                            color="dark"
                                            fullWidth
                                            className="mt-1"
                                            onClick={() => {
                                                handleSlotsChange(localSlots);
                                                setPopoverOpened(false);
                                            }}
                                            loading={updating}
                                        >
                                            {t('save')}
                                        </Button>
                                    </div>
                                </Popover.Dropdown>
                            </Popover>
                        </div>
                    ) : (
                        <div className="text-xs text-gray-500 font-medium">
                            {booking.inventory_slots ?? 1} {t('slots')}
                        </div>
                    )}
                </div>
            </td>
            <td>
                <div className="text-xs">
                    {service.name}
                    {service.name_public && (
                        <div className="text-xxs font-bold text-gray-400 uppercase tracking-wider">
                            {service.name_public}
                        </div>
                    )}
                </div>
            </td>
            {!readOnly && (
                <td className="py-2.5 px-4 text-right flex justify-end">
                    <Button
                        onClick={handleDelete}
                        loading={deleting}
                        size="xs"
                        variant="subtle"
                        color="gray"
                    >
                        <IconTrash size={14} stroke={1} className="text-gray-300" />
                    </Button>
                </td>
            )}
        </tr>
    );
}
